import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, AlertTriangle, Zap, Smile } from "lucide-react";
import {
  WhatsAppCTA,
  OutlineButton,
} from "../components/Buttons";
import { PhoneIconBtn, MailIconBtn } from "../components/ContactIcons";
import Reveal from "../components/Reveal";
import MouseGlow from "../components/MouseGlow";
import Counter from "../components/Counter";
import CoordinationNetwork from "../components/CoordinationNetwork";
import { SITE } from "../lib/site";
import useSEO from "../hooks/useSEO";

const PILLARS = [
  {
    title: "Audit & Assurance",
    body: "Statutory, internal, and forensic audits, including banking and institutional assignments.",
  },
  {
    title: "Tax & Compliance",
    body: "GST, Income Tax, TDS, and end-to-end regulatory compliance.",
  },
  {
    title: "Representation & Dispute Resolution",
    body: "Handling notices, assessments, and representation before tax authorities, including MSME dispute resolution.",
    featured: true,
  },
  {
    title: "Governance & Advisory",
    body: "Internal controls, risk frameworks, and ongoing financial and business advisory.",
  },
  {
    title: "Finance Operations & Specialised Services",
    body: "Bookkeeping, MIS, payroll, RERA compliance, and other ongoing financial support.",
  },
];

const OUTCOMES = [
  {
    icon: ShieldCheck,
    title: "Compliance handled, not chased",
    body: "Filings, audits and notices move through the firm on a defined cadence.",
  },
  {
    icon: FileText,
    title: "Clear, reliable records",
    body: "Books, MIS, and reconciliations that hold up under scrutiny.",
  },
  {
    icon: AlertTriangle,
    title: "Lower risk of notices",
    body: "Issues caught early, structured before they escalate into disputes.",
  },
  {
    icon: Zap,
    title: "Faster resolution",
    body: "When something does come up, response time is hours, not weeks.",
  },
];

const OUTCOME_CLOSER = "And most importantly — one less area for you to think about on a daily basis.";

export default function Home() {
  useSEO({
    title:
      "SVMD & Associates — Chartered Accountants in Coimbatore | Audit, Tax, Representation, MSME Recovery",
    description:
      "Chartered Accountancy firm in Coimbatore handling audit, tax, GST and income tax compliance, representation against notices and assessments, and MSME payment recovery for 1,000+ clients across India.",
    keywords:
      "Chartered Accountants Coimbatore, CA firm Coimbatore, audit firm India, GST compliance, income tax notice representation, MSME payment recovery, statutory audit, Virtual CFO India",
    canonical: "/",
  });
  return (
    <>
      {/* ========== 1. HERO (DARK) ========== */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at top, rgba(37,99,235,0.20) 0%, transparent 60%)" }}
          aria-hidden
        />
        <div className="absolute -right-20 -top-32 w-[680px] h-[680px] rounded-full blur-3xl opacity-25 bg-[radial-gradient(circle,rgba(37,99,235,0.45)_0%,transparent_60%)]" aria-hidden />
        <MouseGlow />

        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-24 md:pb-28 min-h-[80vh] flex flex-col justify-center">
          <Reveal>
            <h1 className="font-display font-semibold text-[44px] sm:text-[60px] md:text-[72px] lg:text-[80px] leading-[1.04] tracking-[-0.03em] max-w-5xl">
              A firm you don&rsquo;t<br className="hidden md:block" />
              have to follow up with.
            </h1>
          </Reveal>

          <Reveal delay={120}>
            <p className="mt-7 max-w-xl text-paper/85 text-base md:text-[18px] leading-[1.55]">
              SVMD &amp; Associates works with{" "}
              <span className="font-mono text-paper">
                <Counter to={1000} suffix="+" />
              </span>{" "}
              clients across India, taking complete ownership of their audit, tax, and compliance requirements.
            </p>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <WhatsAppCTA testId="hero-whatsapp-cta" />
              <OutlineButton
                as={Link}
                to="/services"
                withArrow
                testId="hero-services-link"
              >
                Explore services
              </OutlineButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========== 2. THE PROMISE (LIGHT) ========== */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow mb-6">What we stand for</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[44px] lg:text-[52px] leading-[1.06] tracking-[-0.025em] max-w-4xl">
              Finance and compliance, taken off your desk.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-9 max-w-3xl space-y-4 text-base md:text-[17px] text-slate-muted leading-[1.65]">
              <p>
                Businesses have enough to deal with. Running operations.
                Managing people. Growing revenue. Finance, compliance, and
                regulatory matters often become an additional layer &mdash;
                time-consuming, technical, and difficult to stay on top of.
              </p>
              <p>
                That&rsquo;s where we come in. We take complete ownership of
                this side of the business, so you can focus on running and
                growing it.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========== 3. WHAT WE DO (DARK) ========== */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at bottom, rgba(37,99,235,0.13) 0%, transparent 55%)" }}
          aria-hidden
        />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <Reveal className="max-w-2xl">
              <div className="text-[11px] tracking-[0.08em] uppercase text-paper/60 font-medium mb-4">What we do</div>
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                Our work covers five core areas.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <Link
                to="/services"
                data-testid="home-whatdo-services-link"
                className="link-arrow text-paper/85 hover:text-voltage text-[14px]"
              >
                <span className="link-wipe">See full services</span>
                <ArrowRight size={16} strokeWidth={2} className="arrow" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div
                  data-testid={`home-pillar-${i}`}
                  className="glass-panel rounded-xl p-6 h-full transition-all duration-300"
                >
                  <h3 className="font-display text-[18px] md:text-[20px] leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-paper/85 text-[14px] leading-relaxed">
                    {p.body}
                  </p>
                  {p.featured && (
                    <Link
                      to="/representation-and-dispute"
                      data-testid="home-rep-link"
                      className="mt-5 inline-flex items-center gap-2 text-paper/85 hover:text-voltage text-[13px] font-medium link-wipe transition-colors"
                    >
                      See representation services
                      <ArrowRight size={14} strokeWidth={2} />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 4. COORDINATION NETWORK (LIGHT — breaks the dark pattern) ========== */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <div className="eyebrow mb-4">Coordination</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                  One team.<br />Everyone coordinated.
                </h2>
              </Reveal>
              <Reveal delay={180}>
                <p className="mt-6 text-slate-muted text-base md:text-[17px] leading-relaxed max-w-md">
                  Where required, we coordinate with the company secretaries,
                  lawyers, valuers, and domain specialists you need. You
                  don&rsquo;t have to manage multiple moving parts. We take
                  responsibility for the entire process.
                </p>
              </Reveal>
            </div>

            <Reveal delay={120} className="col-span-12 lg:col-span-7">
              {/* Dark inset frame keeps the network's voltage palette readable on a light section */}
              <div className="relative rounded-2xl overflow-hidden bg-ink p-4 md:p-6 shadow-[0_24px_60px_-24px_rgba(10,22,40,0.35)]">
                <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
                <div
                  className="absolute inset-0"
                  style={{ background: "radial-gradient(ellipse at center, rgba(37,99,235,0.18) 0%, transparent 55%)" }}
                  aria-hidden
                />
                <div className="relative">
                  <CoordinationNetwork />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ========== 5. OUTCOME (DARK — decorative cards) ========== */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at top right, rgba(212,255,63,0.06) 0%, transparent 55%)" }}
          aria-hidden
        />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <Reveal>
                <div className="text-[11px] tracking-[0.08em] uppercase text-paper/60 font-medium mb-4">Outcome</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                  Over time, this leads to.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal key={o.title} delay={i * 90}>
                  <div
                    data-testid={`home-outcome-${i}`}
                    className="relative glass-panel rounded-xl p-6 h-full overflow-hidden group transition-all duration-300 hover:-translate-y-1"
                  >
                    <span aria-hidden className="absolute top-0 left-6 right-6 h-px bg-voltage/40 group-hover:bg-voltage transition-colors" />
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-voltage/10 text-voltage border border-voltage/30">
                        <Icon size={18} strokeWidth={1.6} />
                      </div>
                      <span className="font-mono text-[10px] tracking-[0.18em] text-paper/45">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-5 font-display font-medium text-[18px] md:text-[20px] leading-[1.25] tracking-[-0.01em] text-paper">
                      {o.title}
                    </h3>
                    <p className="mt-3 text-paper/70 text-[13px] md:text-[14px] leading-relaxed">
                      {o.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={500}>
            <div className="mt-10 md:mt-12 max-w-4xl">
              <div className="flex items-start gap-4 md:gap-5 rounded-xl border border-voltage/30 bg-voltage/[0.04] p-6 md:p-7">
                <span aria-hidden className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-md bg-voltage text-ink shrink-0">
                  <Smile size={18} strokeWidth={2} />
                </span>
                <p className="font-display font-medium text-[18px] md:text-[22px] leading-[1.35] tracking-[-0.01em] text-paper">
                  {OUTCOME_CLOSER}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========== 6. CLOSING CTA (FULL-BLEED DARK) ========== */}
      <section className="relative bg-hero-gradient text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl opacity-35 bg-[radial-gradient(circle,rgba(37,99,235,0.4)_0%,transparent_60%)]" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
          <Reveal>
            <h2 className="font-display font-semibold text-[40px] md:text-[64px] lg:text-[76px] leading-[1.04] tracking-[-0.03em]">
              Ready to take it<br />off your desk?
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-7 max-w-2xl mx-auto text-paper/85 text-base md:text-[17px] leading-relaxed">
              If you&rsquo;re looking for a firm that can take complete
              ownership of your finance and compliance requirements, we should
              speak.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-9 flex justify-center items-center gap-3">
              <WhatsAppCTA testId="home-close-whatsapp-cta" />
              <PhoneIconBtn variant="dark" testId="home-close-phone-icon" />
              <MailIconBtn variant="dark" testId="home-close-email-icon" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
